import Checkmark from '@allesauto/design/svg/checkmark.svg';
import CircleSolid from '@allesauto/design/svg/circle_solid.svg';
import Rectangle from '@allesauto/design/svg/rectangle.svg';
import {
  BenefitBackground,
  FieldTypeLink,
  ListMarkerType,
} from '@allesauto/utils/consts';
import {
  getUrlFromStoryblokLink,
  parseSlashToURL,
} from '@allesauto/utils/funcs';
import clsx from 'clsx';
import React from 'react';

import './CheckList.css';

import TextLink from '../../textLink';
interface ICustomClassInterface {
  wrapperClassName?: string;
  checkMarkIconClassName?: string;
  checkItemClassName?: string;
}

export interface ICheckListProps {
  headline?: string;
  subHeadline?: string;
  listItems: string[];
  background?: BenefitBackground;
  markerType?: ListMarkerType;
  textLinkLabel?: string;
  textLink?: FieldTypeLink;
  customClassName?: ICustomClassInterface;
}

export interface CheckListProps {
  list: ICheckListProps;
}

const CheckList: React.FC<CheckListProps> = (props) => {
  const {
    list: {
      headline,
      subHeadline,
      listItems,
      customClassName,
      background = BenefitBackground.DEFAULT,
      markerType = ListMarkerType.CHECKMARK,
      textLinkLabel,
      textLink,
    },
  } = props;

  const textLinkUrl = textLink ? getUrlFromStoryblokLink(textLink) : '';

  const themes: any = {
    PURPLE: {
      wrapper: 'bg-purple80 monochrome:bg-grey90',
      headline: 'text-green60 monochrome:text-white',
      content: 'text-white',
      markerColor: 'text-green60 monochrome:text-white',
      textLink: 'text-white monochrome:text-white',
      fillLink: 'fill-white monochrome:fill-white',
    },
    GREEN: {
      wrapper: 'bg-green60 monochrome:bg-grey40',
      headline: 'text-purple80 monochrome:text-grey90',
      content: 'text-grey100',
      markerColor: 'text-purple80 monochrome:text-grey90',
      textLink: 'text-purple80 monochrome:text-grey90',
      fillLink: 'fill-purple80 monochrome:fill-grey',
    },
    WHITE: {
      wrapper: 'bg-white',
      headline: 'text-purple80 monochrome:text-grey90',
      content: 'text-grey100',
      markerColor: 'text-purple80 monochrome:text-grey90',
      textLink: 'text-purple80 monochrome:text-grey90',
      fillLink: 'fill-purple80 monochrome:fill-grey90',
    },
    GREY: {
      wrapper: 'bg-grey10',
      headline: 'text-purple80 monochrome:text-grey90',
      content: 'text-grey100',
      markerColor: 'text-purple80 monochrome:text-grey10',
      textLink: 'text-purple80 monochrome:text-grey90',
      fillLink: 'fill-purple80 monochrome:fill-grey90',
    },
    BLACK: {
      wrapper: 'bg-grey90',
      headline: 'text-green60 monochrome:text-white',
      content: 'text-white',
      markerColor: 'text-green60 monochrome:text-white',
      textLink: 'text-white monochrome:text-white',
      fillLink: 'fill-white monochrome:fill-white',
    },
    DEFAULT: {
      wrapper: 'bg-grey10',
      headline: 'text-purple80 monochrome:text-grey90',
      content: 'text-grey100',
      markerColor: 'text-purple80 monochrome:text-grey90',
      textLink: 'text-grey100 monochrome:text-grey100',
      fillLink: 'fill-grey100 monochrome:fill-grey100',
    },
  };

  const toIcon = () => {
    switch (markerType) {
      case ListMarkerType.BULLET:
        return <CircleSolid />;
      case ListMarkerType.CHECKMARK:
        return <Checkmark />;
      case ListMarkerType.RECTANGLE:
        return <Rectangle />;
      default:
        return;
    }
  };

  const className = {
    wrapper: clsx(
      'items-center sm:p-30px lg:flex-[2_0_16.6%] lg:p-40px',
      themes[background].wrapper,
      customClassName?.wrapperClassName && customClassName.wrapperClassName
    ),
    header: clsx(
      'mb-30px',
      subHeadline ? 'sm:h-[70px] lg:h-[90px]' : 'sm:h-[80px] lg:h-[90px]'
    ),
    headline: clsx(
      'break-words font-headline font-weight-bold text-headline3',
      themes[background].headline
    ),
    subHeadline: clsx(
      'font-regular font-weight-normal grey-100 mt-10px sm:mb-30px lg:mb-60px',
      themes[background].content
    ),
    listItem: clsx(
      'flex font-regular font-weight-normal grey-100 mb-10px relative text-p1 last:mb-0',
      themes[background].content,
      customClassName?.checkItemClassName && customClassName.checkItemClassName
    ),
    marker: clsx(
      'flex items-start mr-15px mt-5px w-[0.75rem]',
      themes[background].markerColor,
      markerType === ListMarkerType.BULLET && 'checklist-bullet mt-[8px]',
      (markerType === ListMarkerType.CHECKMARK ||
        markerType === ListMarkerType.RECTANGLE) &&
        'checklist',
      customClassName?.checkMarkIconClassName &&
        customClassName.checkMarkIconClassName
    ),
    textLinkContent: 'sm:bottom-30px lg:bottom-40px mt-20px',
    textLink: clsx(
      background === BenefitBackground.BLACK && 'benefit-link-black',
      background === BenefitBackground.GREEN &&
        'benefit-link benefit-link-green',
      background === BenefitBackground.PURPLE && 'benefit-link-purple',
      background === BenefitBackground.WHITE && 'benefit-link-white',
      background === BenefitBackground.GREY && 'benefit-link-grey',
      themes[background].textLink
    ),
    listItemText: 'w-full',
  };

  return (
    <div className={className.wrapper}>
      {(headline || subHeadline) && (
        <div className={className.header}>
          {headline && <h2 className={className.headline}>{headline}</h2>}
          {subHeadline && (
            <p className={className.subHeadline}>{subHeadline}</p>
          )}
        </div>
      )}
      <ul>
        {listItems?.map((listItem, idx) => (
          <li key={`listItem-${idx}`} className={className.listItem}>
            <span className={className.marker}>{toIcon()}</span>
            <span
              className={className.listItemText}
              dangerouslySetInnerHTML={{
                __html: listItem,
              }}
            ></span>
          </li>
        ))}
      </ul>
      {textLinkLabel && textLinkUrl && (
        <div className={className.textLinkContent}>
          <TextLink
            text={textLinkLabel}
            href={parseSlashToURL(textLinkUrl)}
            target={textLink?.target ?? '_self'}
            textColor={themes[background].textLink}
            fillColor={themes[background].fillLink}
            customClassName={className.textLink}
            isDarkBg={
              background === BenefitBackground.BLACK ||
              background === BenefitBackground.PURPLE
            }
          />
        </div>
      )}
    </div>
  );
};

export default CheckList;
